
import React from 'react';
import { Button, SectionTitle, ScrollNavigation, ScrollReveal } from './Components';

export const HomePage: React.FC<{ setPage: (p: string) => void }> = ({ setPage }) => {
  const features = [
    { 
      icon: "fa-stethoscope", 
      title: "Expert Doctors", 
      desc: "Access a wide network of UK & US qualified doctors across various specialties.",
      color: "from-[#008080] to-[#006666]"
    },
    { 
      icon: "fa-video", 
      title: "Live Video Consultations", 
      desc: "Connect with your doctor through secure, high-quality video calls from anywhere.",
      color: "from-rose-500 to-rose-600"
    },
    { 
      icon: "fa-calendar-check", 
      title: "Easy Appointment Booking", 
      desc: "Find available slots and book your appointments in just a few clicks, 24/7.",
      color: "from-[#008080] to-[#006666]"
    },
    { 
      icon: "fa-shield-halved", 
      title: "Secure & Confidential", 
      desc: "Your health data is private and protected with industry-leading security.",
      color: "from-rose-500 to-rose-600"
    },
    { 
      icon: "fa-pills", 
      title: "Online Prescriptions", 
      desc: "Receive digital prescriptions from your doctor.",
      color: "from-[#008080] to-[#006666]"
    },
    { 
      icon: "fa-file-medical", 
      title: "Medical Records Access", 
      desc: "View your complete medical history, lab results, and treatment plans in one place.",
      color: "from-rose-500 to-rose-600"
    },
    { 
      icon: "fa-mobile-screen-button", 
      title: "Multi-Platform Access", 
      desc: "Use our service on your computer, tablet, or mobile phone with ease.",
      color: "from-[#008080] to-[#006666]"
    },
    { 
      icon: "fa-headset", 
      title: "24/7 Customer Support", 
      desc: "Our dedicated support team is here to help you around the clock.",
      color: "from-rose-500 to-rose-600"
    }
  ];

  const scrollToNextSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="animate-in fade-in duration-700 relative">
      {/* Background Ambient Blobs */}
      <div className="absolute top-0 right-0 w-[300px] md:w-[500px] h-[300px] md:h-[500px] bg-indigo-200/30 rounded-full blur-[80px] md:blur-[120px] -z-10 translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 left-0 w-[400px] md:w-[600px] h-[400px] md:h-[600px] bg-indigo-200/30 rounded-full blur-[100px] md:blur-[140px] -z-10 -translate-x-1/2 translate-y-1/2"></div>

      {/* Hero Section */}
      <section id="hero" className="relative pt-12 md:pt-24 pb-16 md:pb-24 px-6 overflow-hidden">
        <ScrollReveal direction="up" delay={0.1}>
          <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            <div className="lg:w-1/2 z-10 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#008080]/10 border border-[#008080]/20 mb-8 animate-bounce-slow shadow-sm">
                <span className="w-2 h-2 rounded-full bg-[#008080]"></span>
                <span className="text-[14px] font-semibold text-[#008080]">Compassionate Virtual Care</span>
              </div>
              
              <h1 className="mb-8 space-y-4">
                <span className="block text-4xl md:text-[48px] font-bold text-[#008080] leading-tight">
                  ROVITA Virtual Clinic
                </span>
                <span className="block text-2xl md:text-[32px] font-semibold text-slate-700 leading-tight">
                  Trusted guidance for better health.
                </span>
              </h1>
              <p className="text-lg md:text-xl text-slate-700 font-medium leading-relaxed max-w-2xl mx-auto lg:mx-0 mb-10">
                Rovita Virtual clinic is an International online medical consultation service providing trusted medical advice, second opinions and health guidance from experienced and qualified doctors.
              </p>
              <div className="flex flex-col sm:flex-row gap-5 justify-center lg:justify-start mt-14">
                <Button onClick={() => setPage('register')} ariaLabel="Book Appointment Now" variant="cta" className="py-5 px-12 text-xl shadow-xl shadow-[#008080]/20">Book Now</Button>
                <Button onClick={() => setPage('doctors')} ariaLabel="Meet Our Board-Certified Doctors" variant="ghost" className="py-5 px-12 text-xl bg-white/50 backdrop-blur-sm">Meet Our Doctors</Button>
              </div>
            </div>
            <div className="lg:w-1/2 relative w-full px-4 md:px-8 scale-up-center">
              {/* Wrapper to contain floating cards relative to the main logo card */}
              <div className="relative max-w-lg mx-auto">
                 <div className="relative rounded-[3rem] w-full aspect-square md:aspect-[4/3] lg:aspect-auto lg:h-[500px] overflow-hidden shadow-2xl border-8 border-white bg-gradient-to-br from-[#008080] to-[#004d4d] flex items-center justify-center group">
                   <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1516549655169-df83a0774514?auto=format&fit=crop&q=80&w=600')] opacity-20 mix-blend-overlay group-hover:scale-110 transition-transform duration-700"></div>
                    <div className="relative z-10 flex flex-col items-center w-full h-full justify-center">
                      <div className="w-[88%] h-[88%] bg-white p-6 rounded-[4.5rem] shadow-2xl flex items-center justify-center overflow-hidden transition-all duration-500 border-4 border-[#008080]/5">
                        <img src="/logo.png" className="w-[92%] h-[92%] object-contain" alt="ROVITA Logo" loading="lazy" />
                      </div>
                    </div>
                 </div>
                 
                 {/* Floating Trust Card 1 */}
                 <div className="absolute top-4 -left-2 md:-top-4 md:-left-8 lg:-left-16 bg-white/95 backdrop-blur-md p-3 md:p-5 rounded-[1.5rem] md:rounded-[2rem] shadow-2xl flex items-center gap-3 md:gap-5 animate-float border border-white z-30 scale-75 md:scale-90 lg:scale-100">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-[#008080] text-white rounded-full flex items-center justify-center text-sm md:text-lg shadow-lg shadow-[#008080]/30"><i className="fa-solid fa-check"></i></div>
                    <div className="text-left">
                      <p className="text-sm md:text-base font-bold text-slate-900 border-none">Certified</p>
                      <p className="text-[10px] md:text-[12px] text-slate-500 font-bold uppercase tracking-wider">UK & US Qualified Doctors</p>
                    </div>
                 </div>
                 
                 {/* Floating Trust Card 2 */}
                 <div className="absolute bottom-4 -right-2 md:-bottom-8 md:-right-8 lg:-right-16 bg-white/95 backdrop-blur-md p-3 md:p-5 rounded-[1.5rem] md:rounded-[2rem] shadow-2xl flex items-center gap-3 md:gap-5 animate-float border border-white hidden sm:flex z-30 scale-75 md:scale-90 lg:scale-100" style={{ animationDelay: '1s' }}>
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-[#008080] text-white rounded-full flex items-center justify-center text-sm md:text-lg shadow-lg shadow-[#008080]/30"><i className="fa-solid fa-globe"></i></div>
                    <div className="text-left">
                      <p className="text-sm md:text-base font-bold text-slate-900 border-none">Global Access</p>
                      <p className="text-[10px] md:text-[12px] text-slate-500 font-bold uppercase tracking-wider">Worldwide Doctors</p>
                    </div>
                 </div>
              </div>
            </div>
          </div>
        </ScrollReveal>
        
        {/* Scroll Down Button moved to ScrollNavigation */}
        <ScrollNavigation nextId="welcome" isLast={false} />
      </section>

      {/* Welcome Section */}
      <section id="welcome" className="py-16 md:py-20 px-6 relative z-10">
         <ScrollReveal direction="up" delay={0.2}>
           <div className="max-w-6xl mx-auto">
              <div className="bg-white p-10 md:p-16 rounded-[3rem] shadow-xl shadow-slate-200/50 border border-slate-100 relative overflow-hidden group hover:shadow-2xl transition-all duration-700">
                  {/* Decorative background element */}
                  <div className="absolute top-0 right-0 w-64 h-64 bg-[#e6f2f2]/50 rounded-bl-[100%] -z-10"></div>
                  <div className="absolute bottom-0 left-0 w-40 h-40 bg-[#f8fafc] rounded-tr-[100%] -z-10"></div>
                  
                  <div className="flex flex-col md:flex-row items-start gap-10 md:gap-14">
                      <div className="w-20 h-20 md:w-24 md:h-24 bg-[#008080] rounded-[2rem] flex items-center justify-center text-white text-4xl md:text-5xl shrink-0 shadow-lg shadow-[#008080]/20 rotate-3 group-hover:rotate-6 transition-transform">
                          <i className="fa-solid fa-hand-holding-medical"></i>
                      </div>
                      
                      <div className="space-y-6 md:space-y-8">
                          <h2 className="text-3xl md:text-5xl font-bold text-slate-900 tracking-tight leading-tight">
                              Welcome to <span className="text-[#008080]">ROVITA</span>
                          </h2>
                          
                          <div className="text-slate-700 font-medium leading-relaxed space-y-6">
                              <p className="text-2xl md:text-3xl text-slate-700 font-semibold leading-tight mb-8">
                                  Guiding you to better health with world-class medical expertise—wherever you are.
                              </p>
                              <p className="text-lg md:text-xl">
                                  We are a global telemedicine platform offering online medical consultations, second opinions, and comprehensive medical advice from a panel of highly qualified, board-certified doctors from the <span className="text-[#008080] font-semibold bg-[#e6f2f2]/50 px-2 py-0.5 rounded-lg">United Kingdom, United States, and beyond</span>.
                              </p>
                              <p className="text-lg md:text-xl">
                                  Our mission is to bridge the gap between patients and top-tier healthcare professionals, ensuring timely, accurate, and personalized medical guidance from the comfort of your home. Whether you’re seeking clarity on a diagnosis, looking for a trusted second opinion, or need expert medical advice without the wait, ROVITA is your trusted health partner. We can help removing your uncertainties.
                              </p>
                          </div>
                      </div>
                  </div>
                  
                  <ScrollNavigation nextId="why-rovita" prevId="hero" />
              </div>
           </div>
         </ScrollReveal>
      </section>

      {/* Why ROVITA Section */}
      <section id="why-rovita" className="bg-slate-50/50 backdrop-blur-sm py-16 md:py-20 px-6 border-y border-slate-100">
        <ScrollReveal direction="up" delay={0.1}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16 md:mb-20">
              <SectionTitle centered title="Why ROVITA?" subtitle="Our platform is designed to provide a complete and convenient telemedicine experience, prioritizing clinical precision and patient comfort." />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {features.map((f, i) => (
                <ScrollReveal key={i} direction="up" delay={i * 0.08}>
                  <div 
                    className="bg-white p-8 md:p-10 rounded-[2rem] md:rounded-[2.5rem] shadow-sm border border-slate-100 hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 text-center group h-full"
                  >
                    <div className={`w-14 h-14 md:w-16 md:h-16 mx-auto bg-gradient-to-br ${f.color} rounded-2xl flex items-center justify-center text-xl md:text-2xl mb-6 md:mb-8 text-white shadow-lg group-hover:scale-110 transition-transform duration-500`}>
                      <i className={`fa-solid ${f.icon}`}></i>
                    </div>
                    <h3 className="text-xl md:text-2xl font-black text-slate-900 mb-3 md:mb-4">{f.title}</h3>
                    <p className="text-slate-700 text-lg md:text-xl font-medium leading-relaxed">
                      {f.desc}
                    </p>
                  </div>
                </ScrollReveal>
              ))}
            </div>

            <ScrollNavigation nextId="how-it-works" prevId="hero" />
          </div>
        </ScrollReveal>
      </section>

      {/* Call to Action Section */}
      <section id="how-it-works" className="py-16 md:py-24 px-6 bg-white text-center relative">
        <ScrollReveal direction="up" delay={0.1}>
          <div className="max-w-6xl mx-auto">
            
            <div className="mb-20">
              <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-16 tracking-tight">How It Works</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-6 relative">
                 {/* Decorative line for desktop */}
                 <div className="hidden md:block absolute top-8 left-0 w-full h-1 bg-slate-50 -z-10 rounded-full"></div>

                 {[
                   "Sign Up & Upload Records",
                   "Choose a Doctor or Service",
                   "Book Your Slot",
                   "Have a Video Consultation",
                   "Get Your Report & Follow-Up Plan"
                 ].map((step, idx) => (
                   <ScrollReveal key={idx} direction="up" delay={idx * 0.1}>
                     <div className="flex flex-col items-center group cursor-default">
                        <div className="w-16 h-16 bg-white border-4 border-slate-50 text-[#008080] rounded-2xl flex items-center justify-center text-xl font-black mb-6 shadow-lg shadow-[#008080]/10 group-hover:scale-110 group-hover:border-[#008080]/10 transition-all duration-300 z-10">
                            {idx + 1}
                        </div>
                        <p className="font-bold text-slate-700 text-lg md:text-xl leading-tight max-w-[200px] group-hover:text-[#008080] transition-colors duration-300">{step}</p>
                     </div>
                   </ScrollReveal>
                 ))}
              </div>
            </div>

            <div className="flex justify-center items-center flex-col gap-8">
               <Button 
                  onClick={() => setPage('register')}
                  className="px-10 md:px-14 py-6 md:py-8 rounded-[1.5rem] md:rounded-[2rem] text-lg md:text-xl w-full md:w-auto"
               >
                  Get Started Now
               </Button>

               <ScrollNavigation prevId="why-rovita" isLast />
            </div>
          </div>
        </ScrollReveal>
      </section>

      <style>{`
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};
